import { Button, Typography } from '@mui/material';
import { isEmpty, map, reduce } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataGrid, DataGridColumnDefinition } from 'components';
import { DefaultPaymentTypeCode } from 'enums';
import { useQueryColumns, useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { BankAggregatedMonitoringItem, BaseMonitoringDataRow } from 'types';
import { orderMonitoringUtils } from 'utils';

type Props = {
  monitoringData?: BankAggregatedMonitoringItem[];
  availablePaymentTypes?: DefaultPaymentTypeCode[];
  className?: string;
};

const ROWS_LIMIT = 5;

export const PayoutOrdersBanksMonitoring: React.FC<Props> = ({
  monitoringData = [],
  availablePaymentTypes = [],
  className,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders_monitoring',
  });
  const { visibleColumns } = useQueryColumns();
  const { getBankName } = useUserContextUtils();
  const [isExpanded, setIsExpanded] = useState(false);

  const banksData = useMemo(() => {
    const banks = reduce(
      monitoringData,
      (acc, item) => {
        const prev = acc[item.bankId] || {};
        acc[item.bankId] = {
          ...prev,
          [item.paymentTypeCode]: {
            alertLevel: item.alertLevel,
            label: item.label,
          },
        };
        return acc;
      },
      {} as Record<string, BaseMonitoringDataRow>,
    );
    return banks;
  }, [monitoringData]);

  const columns: DataGridColumnDefinition[] = useMemo(() => {
    const paymentTypeColumns = map(availablePaymentTypes, (paymentType) => ({
      header: orderMonitoringUtils.getPaymentTypeHeader(paymentType),
      valueGetter: (item: any) => {
        const data = item[paymentType];
        return orderMonitoringUtils.getAmountRangeCell(data);
      },
      hidden: visibleColumns?.[paymentType] === false,
      valueClassName: '!tw-font-bold !tw-p-0',
    }));

    return [
      {
        header: t('columns.bank'),
        valueGetter: (item) => getBankName({ id: item.bankId }),
      },
      ...paymentTypeColumns,
    ];
  }, [availablePaymentTypes, visibleColumns, getBankName, t]);

  const rows = useMemo(
    () =>
      reduce(
        Object.entries(banksData),
        (acc, [bankId, item]) => {
          acc.push({
            bankId,
            ...item,
          });
          return acc;
        },
        [] as any[],
      ),
    [banksData],
  );

  const visibleRows = useMemo(() => {
    if (isExpanded) {
      return rows;
    }
    return rows.slice(0, ROWS_LIMIT);
  }, [isExpanded, rows]);

  if (isEmpty(visibleRows)) {
    return null;
  }

  return (
    <div className={className}>
      <Typography className="tw-pb-2" variant="h4">
        {t('sections.banks')}
      </Typography>

      <DataGrid
        tableStyle={{ tableLayout: 'fixed' }}
        columns={columns}
        data={visibleRows}
      />
      {rows.length > ROWS_LIMIT && (
        <Button
          variant="text"
          onClick={() => setIsExpanded(!isExpanded)}
          className="tw-mt-2"
        >
          {isExpanded
            ? t('collapse_toggle.collapse')
            : t('collapse_toggle.expand')}
        </Button>
      )}
    </div>
  );
};
